import { plainMessageText } from '../../netlify/functions/lib/messageFormatting.mjs';
import { jrStaffMentionRecipientUids } from './jrStaffRoster.ts';
import type { DiscussionAudienceGroup, UserDoc, UserRole } from './users.ts';
import { userHandle } from './threadLabels.ts';

const STAFF_ROLES: UserRole[] = ['director', 'manager', 'company', 'ensemble', 'foh'];

/**
 * The audience fields needed to decide whether a person participates in a
 * discussion. Directors may administer every board, but administration is not
 * participation: a private Intern post only expects people explicitly placed
 * in the Interns group.
 */
export interface DiscussionAudienceLike {
  audienceRoles?: UserRole[];
  audienceGroups?: DiscussionAudienceGroup[];
  boardKey?: 'executiveTeachers';
}

function isActiveAdultAccount(user: UserDoc | null | undefined): user is UserDoc {
  if (!user || user.status !== 'active' || user.retiredFirebaseDancer === true) return false;
  const childOnlyDancer = typeof user.studentId === 'string'
    && user.studentId.trim().length > 0
    && !STAFF_ROLES.includes(user.role);
  return !childOnlyDancer;
}

/**
 * True when this account is an expected participant, notification recipient,
 * acknowledgement target, or mention candidate for this discussion.
 *
 * This is deliberately narrower than read or moderation access. In
 * particular, a Director can open and administer the Intern Board without
 * being included here unless their account also has groups: ['interns'].
 */
export function discussionAudienceIncludesUser(
  discussion: DiscussionAudienceLike,
  user: UserDoc | null | undefined
): boolean {
  if (!isActiveAdultAccount(user)) return false;

  const groups = discussion.audienceGroups ?? [];
  if (groups.includes('interns')) return (user.groups ?? []).includes('interns');

  const roles = discussion.audienceRoles ?? [];
  if (discussion.boardKey === 'executiveTeachers') {
    return ['director', 'manager', 'company'].includes(user.role);
  }
  // A named group used to short-circuit the role check here. It could not
  // change an answer while 'interns' (handled above) was the only group any
  // discussion could carry, and it would be WRONG now: 'jrStaff' is a group
  // ON TOP OF a role audience, so a Jr Staff account holder must still match
  // by role. Guardians on the roll are answered by discussionReachesUser
  // below, not here - see its own comment for why the two are separate.
  if (!STAFF_ROLES.includes(user.role)) return false;
  return roles.length === 0 || roles.includes(user.role);
}

/**
 * WHO THE POST REACHES, which is wider than who PARTICIPATES in it.
 *
 * A staff announcement addressed to Jr Staff also reaches the parents
 * standing in for the Jr Staff dancers who have no staff account of their
 * own (Bobbie, 2026-08-30: "Can we fix the announcements go to the correct
 * people when selecting Jr staff?"). Those guardians READ the announcement
 * and are notified about it; they do not acknowledge it, reply to it, or
 * appear on its ack roster, so they are deliberately NOT
 * discussionAudienceIncludesUser. Two questions, two functions - collapsing
 * them would either hand a parent an Understood button the rules refuse or
 * leave a Director's ack list quietly counting people who were never asked.
 *
 * `onJrStaffRoll` is the caller's membership of jrStaffBoardMembership/current
 * (useJrStaffBoardMembership). NOTHING HERE IS A PERMISSION: the boundary is
 * jrStaffAudienceCoversRollCaller in shared/firestore.rules.
 *
 * `jrStaffBoardRemoved` (PR #807 round 8, Codex P1-a) is the Manage members
 * overlay's OWN removedUids.includes(uid), threaded in as its own input
 * rather than folded into onJrStaffRoll: every Jr Staff account also
 * carries a matching STAFF_ROLE, so discussionAudienceIncludesUser's plain
 * role match above admits a removed member regardless of roll or overlay
 * state, and this function never reached its own onJrStaffRoll check to
 * find out they had been removed. A director/manager removing someone
 * from Manage members must take effect on the NEXT announcement rendered,
 * not just future roll sweeps - that is this PR's own guide claim. So the
 * removal fence runs FIRST, and only for announcements whose audience is
 * actually the Jr Staff group: `audienceGroups.includes('jrStaff') &&
 * boardKey !== 'executiveTeachers'` is the same unambiguous signal
 * discussionAudienceIncludesUser's own comment above and notify.mjs's
 * isJrStaffAnnouncement already use, never a broad role audience that
 * merely happens to include Jr Staff roles - NewMessage.tsx's
 * announcementIncludesJrStaff stamps audienceGroups: ['jrStaff'] onto ANY
 * audience that includes the ensemble role, so there is no data shape
 * where "role-matches Jr Staff" and "was this Jr Staff announcement's
 * selected audience" can disagree. A role-audience post NOT scoped to Jr
 * Staff is untouched: a removed Jr Staff board member who still holds
 * (say) a manager role keeps seeing manager-addressed posts, exactly as
 * they should.
 *
 * THIS IS DISPLAY ONLY, same as the rest of this function - it decides
 * what usePersonalDiscussions folds into a Messages list, nothing about
 * what the rules allow. The rules-level removal deny was tried and
 * reverted earlier in this same PR (a per-document removedUids check is
 * not query-provable, so Firestore denies the WHOLE listener rather than
 * filtering one document out of it); the accepted gap - a removed member
 * can still ack/reply to a post their role matches, because the rules
 * still permit it - is unchanged by this fix and remains tracked as queue
 * task 35 (the deferred read model). This fix only stops the removed
 * member's OWN Messages list from showing them a Jr Staff announcement
 * they no longer belong to; it is not, and was never meant to be, the
 * enforcement boundary.
 */
export function discussionReachesUser(
  discussion: DiscussionAudienceLike,
  user: UserDoc | null | undefined,
  onJrStaffRoll = false,
  jrStaffBoardRemoved = false
): boolean {
  const isJrStaffAudience = (discussion.audienceGroups ?? []).includes('jrStaff')
    && discussion.boardKey !== 'executiveTeachers';
  if (isJrStaffAudience && jrStaffBoardRemoved) return false;
  if (discussionAudienceIncludesUser(discussion, user)) return true;
  if (!onJrStaffRoll || !isActiveAdultAccount(user)) return false;
  return isJrStaffAudience;
}

/**
 * 'admitted': confirmed a live reach, matching every fence notify.mjs
 * applies at the last mile. 'rejected': confirmed NOT reached (paused,
 * retired, dancer-shaped, or not on any of the three admitting sets at
 * all). 'unknown': this composer's own directory read cannot see the
 * candidate's user doc at all, so its live status is UNVERIFIABLE, not
 * assumed good - see jrStaffAnnouncementReachAdmitsCandidate's own comment
 * for why an exact count is not always computable client-side.
 */
export type JrStaffReachAdmission = 'admitted' | 'rejected' | 'unknown';

/**
 * WHO A NOT-YET-SENT JR STAFF ANNOUNCEMENT WILL ACTUALLY REACH, mirroring
 * notify.mjs's isJrStaffAnnouncementRecipient one candidate at a time (P2, PR
 * #807 round 3): NewMessage's "Reaches: N people" preview was unioning raw
 * uid sets (announcementStaff, the roll, the overlay's addedUids) with no
 * check that any of those accounts were still LIVE, so it could claim a
 * paused guardian, an account made inactive after being overlay-added, a
 * retired Firebase dancer, or a dancer-shaped login. Kim reads this number
 * before Send.
 *
 * NOT THE SECURITY BOUNDARY - notify.mjs re-derives this from its own Admin
 * SDK reads before anything actually sends. THIS IS THE FOURTH ROUND OF
 * FINDINGS on this preview (ignored the overlay, then conflated empty with
 * unresolved, then missed these live-account fences, now this one) - each a
 * genuinely different defect, which is the signal that an EXACT count is not
 * reliably computable client-side for every sender role, not a signal to
 * keep narrowing. So this returns 'unknown' rather than guessing, for the
 * one gap that cannot close by construction of what a browser can see:
 * `candidateData` is `undefined` whenever this composer's own users read
 * cannot see the account at all (an 'ensemble' or 'company' sender's
 * useUsersList query is STAFF_ROLES-only, so it never carries a family doc -
 * see users/{uid}'s own read rule). The caller (NewMessage.tsx) must show an
 * honest lower bound rather than a confident wrong number whenever any
 * candidate comes back 'unknown' - see its own comment at the call site.
 *
 * One gap stays closed to every role regardless: `familyLinkRevocations/{uid}`
 * is `allow read, write: if false` in shared/firestore.rules with no
 * exception, Director included, so a revoked roll guardian is 'admitted'
 * here even though notify.mjs will still refuse her - the number this
 * returns is a ceiling on the roll-only branch, same as it always was.
 *
 * @param candidateData This composer's own read of the candidate's user doc,
 *   or undefined when that doc is not in this sender's own directory.
 * @param membership Which of the three uid sets actually admitted this
 *   candidate - the fences differ by branch, matching the server exactly:
 *   a staff-role match or an explicit Manage-members add needs only
 *   `status == 'active'` (an overlay add carries no further check at all,
 *   staff role skips the dancer-shape check because it is impossible for a
 *   real staff account with a role match); a roll-only guardian additionally
 *   needs the strict dancer-login and retirement fences.
 */
export function jrStaffAnnouncementReachAdmitsCandidate(
  candidateData: Pick<UserDoc, 'status' | 'retiredFirebaseDancer' | 'studentId'> | null | undefined,
  membership: { staffMatch: boolean; overlayAdded: boolean; onRoll: boolean }
): JrStaffReachAdmission {
  if (!membership.staffMatch && !membership.overlayAdded && !membership.onRoll) return 'rejected';
  // Unreadable: this candidate's live status cannot be verified from this
  // composer's own directory. Honest about it rather than assuming either
  // direction - the caller decides how to present that uncertainty.
  if (!candidateData) return 'unknown';
  if (candidateData.status !== 'active') return 'rejected';
  if (membership.overlayAdded) return 'admitted';
  if (membership.staffMatch) {
    return candidateData.retiredFirebaseDancer !== true ? 'admitted' : 'rejected';
  }
  // Roll-only: the strict studentId fence applies even to a staff-role
  // holder who also carries one (a promoted dancer) - the one shape
  // isJrStaffAnnouncementRecipient checks studentId ahead of the milder
  // dancer-login helper for, so this matches its exact branch order rather
  // than reusing isActiveAdultAccount's more lenient version above.
  if (typeof candidateData.studentId === 'string' && candidateData.studentId.trim().length > 0) {
    return 'rejected';
  }
  return candidateData.retiredFirebaseDancer !== true ? 'admitted' : 'rejected';
}

// --- Reply mention parsing ---------------------------------------------
//
// Moved here from StaffAnnouncementRow.tsx (Codex round 4, PR #807 Finding
// A) so a node test can execute it directly rather than pattern-matching
// its source text - this file, like threadLabels.ts (userHandle's real
// home), pulls no React or Firebase client graph.

/**
 * Reply shortcuts. @junior and @ensemble resolve to current Firebase-capable
 * Jr Staff identities below (jrStaffMentionRecipientUids): one human is named
 * once in the composer, but BOTH of her active staff sign-ins are notified, so
 * the mention reaches whichever account she opens. Sending from either sign-in
 * excludes both. Each resolved uid is verified server-side by discussionWrite,
 * so a Jr Staff paused between the picker snapshot and the send refuses the
 * whole reply with mention_not_allowed rather than notifying a paused account.
 * A cookie-only dancer login cannot read a Firebase message and stays outside
 * this direct-mention lane; no new authority is inferred from a designation.
 */
const ROLE_MENTION_ALIASES: Array<{ handle: string; roles: UserRole[] }> = [
  { handle: 'all', roles: STAFF_ROLES },
  { handle: 'staff', roles: STAFF_ROLES },
  { handle: 'teachers', roles: ['manager', 'company', 'ensemble'] },
  { handle: 'teacher', roles: ['manager', 'company', 'ensemble'] },
  { handle: 'managers', roles: ['manager'] },
  { handle: 'manager', roles: ['manager'] },
  { handle: 'executive', roles: ['company'] },
  { handle: 'company', roles: ['company'] },
  { handle: 'junior', roles: ['ensemble'] },
  { handle: 'ensemble', roles: ['ensemble'] },
  { handle: 'admin', roles: ['foh'] },
  { handle: 'frontdesk', roles: ['foh'] },
  { handle: 'director', roles: ['director'] },
];

function hasMentionHandle(text: string, handle: string): boolean {
  const escaped = handle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|\\s)@${escaped}\\b`, 'i').test(text);
}

/**
 * Parses @handles out of a Staff Board reply into the uids and role
 * aliases to mention. `users` is this composer's own visible directory
 * (StaffAnnouncementRow.tsx's activeStaffUsers), never the roll: a jrStaff
 * announcement's stand-in guardians are not staff and are never valid
 * mention targets, matching discussionWrite.mjs's own
 * discussionMentionAllows exactly (no groups check on that shape either).
 *
 * DIRECT-HANDLE VISIBILITY IS GROUP-GATED ONLY FOR 'interns' (Finding A,
 * Codex round 4). 'jrStaff' is deliberately never stored in anyone's own
 * `groups` - it names a role audience widened by a roll on a DIFFERENT
 * document, the discussion's own audienceGroups, never the user doc. Gating
 * the per-candidate visibility check on `audienceGroups.length > 0` (any
 * group at all) treated a jrStaff post the same as a private Intern one and
 * discarded every direct-handle candidate, so @Kim on a Jr Staff
 * announcement reply silently mentioned nobody. Only 'interns' actually
 * means "look at the user's own groups"; every other shape, jrStaff
 * included, means what it always did - visible by role.
 */
function parseReplyMentions(
  text: string,
  users: UserDoc[],
  currentUid: string,
  audienceRoles: UserRole[],
  audienceGroups: DiscussionAudienceGroup[]
): { mentionUids: string[]; mentionRoles: UserRole[] } {
  text = plainMessageText(text);
  const visibleRoles = new Set(audienceRoles.length ? audienceRoles : STAFF_ROLES);
  const uidSet = new Set<string>();
  const roleSet = new Set<UserRole>();

  // 'interns' is the PRIVATE audience where a role mention makes no sense.
  // A 'jrStaff' announcement is an ordinary role audience with the stand-in
  // parents added, so @teachers and friends still mean what they always did.
  for (const alias of audienceGroups.includes('interns') ? [] : ROLE_MENTION_ALIASES) {
    if (!hasMentionHandle(text, alias.handle)) continue;
    if (alias.handle === 'junior' || alias.handle === 'ensemble') {
      if (visibleRoles.has('ensemble')) {
        for (const uid of jrStaffMentionRecipientUids(users, currentUid)) uidSet.add(uid);
      }
      continue;
    }
    alias.roles
      .filter((role) => visibleRoles.has(role))
      .forEach((role) => roleSet.add(role));
  }

  for (const u of users) {
    if (u.uid === currentUid) continue;
    const visible = audienceGroups.includes('interns')
      ? (u.groups ?? []).some((group) => audienceGroups.includes(group))
      : visibleRoles.has(u.role as UserRole);
    if (!visible) continue;
    const handle = userHandle(u).toLowerCase();
    if (handle && hasMentionHandle(text, handle)) uidSet.add(u.uid);
  }

  return { mentionUids: [...uidSet], mentionRoles: [...roleSet] };
}

export { parseReplyMentions, hasMentionHandle, ROLE_MENTION_ALIASES };
